import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FilterMatchMode } from 'primereact/api';
import {
    useGetPartsDealerBrandMatchListQuery,
    useGetPartsDealerBrandMatchDealerDropdownQuery,
    useGetPartsDealerBrandMatchBrandDropdownQuery,
    useSavePartsDealerBrandMatchMutation,
    useUpdatePartsDealerBrandMatchVisibilityMutation,
    useDeletePartsDealerBrandMatchMutation
} from '../../../../api/endpoints/partsDealerBrandMatch';
import { useGetLookUpDropdownListQuery } from '../../../../api/endpoints/lookup';
import { useLanguage } from '../../../../contexts/LanguageContext';

const SORT_FIELD_MAP = {
    dealerId: 'dealerId',
    dealerName: 'dealerName',
    dealerType: 'dealerType',
    brand: 'brand',
    isVisible: 'isVisible',
    isActive: 'isActive',
    createdDate: 'createdDate',
    createdName: 'createdName'
};

const SEARCH_DEBOUNCE_MS = 500;

const INITIAL_TABLE_FILTERS = {
    dealerId: null,
    dealerName: null,
    dealerNameMatchMode: FilterMatchMode.CONTAINS,
    brand: null,
    brandMatchMode: FilterMatchMode.CONTAINS,
    isVisible: null,
    dealerType: null,
    activeOnly: 1,
    createdName: null,
    createdNameMatchMode: FilterMatchMode.CONTAINS
};

const buildApiFilters = (filters) => {
    const api = {};

    if (filters.dealerId) api.dealerId = filters.dealerId;
    if (filters.brand) api.brand = filters.brand;
    if (filters.isVisible !== null && filters.isVisible !== undefined) api.isVisible = filters.isVisible;
    if (filters.dealerType !== null && filters.dealerType !== undefined) api.dealerType = filters.dealerType;
    if (filters.activeOnly !== null && filters.activeOnly !== undefined) api.activeOnly = filters.activeOnly;

    const searchParts = [filters.dealerName, filters.createdName].filter((value) => value !== null && value !== undefined && String(value).trim() !== '');
    if (searchParts.length) {
        api.searchText = searchParts.join(' ').trim();
    }

    if (api.activeOnly === undefined) {
        api.activeOnly = 1;
    }

    return api;
};

const usePartsDealerBrandMatch = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const toast = useRef(null);
    const dealerDebounceRef = useRef(null);
    const brandDebounceRef = useRef(null);
    const tableDealerDebounceRef = useRef(null);
    const selectedDealerOptionMapRef = useRef({});
    const selectedBrandOptionMapRef = useRef({});

    const languageId = getCurrentLanguageId();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedDealerIds, setSelectedDealerIds] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [isVisible, setIsVisible] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const [dealerSearchText, setDealerSearchText] = useState('');
    const [brandSearchText, setBrandSearchText] = useState('');
    const [tableDealerSearchText, setTableDealerSearchText] = useState('');
    const [dealerFilterValue, setDealerFilterValue] = useState('');

    const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10, sortField: null, sortOrder: null });
    const [activeFilters, setActiveFilters] = useState(INITIAL_TABLE_FILTERS);

    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const apiFilters = useMemo(() => buildApiFilters(activeFilters), [activeFilters]);

    const {
        data: lookupData = [],
        isLoading: isLookupLoading,
        isFetching: isLookupFetching
    } = useGetLookUpDropdownListQuery({
        GroupCode: 'PARTS_DEALER_BRAND_VISIBILITY',
        LanguageId: languageId
    });

    const visibilityOptions = useMemo(
        () =>
            [...(lookupData || [])]
                .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
                .map((item) => ({
                    label: item.translatedText,
                    value: Number(item.code)
                }))
                .filter((item) => !Number.isNaN(item.value)),
        [lookupData]
    );

    const isLookupEmpty = !isLookupLoading && !isLookupFetching && visibilityOptions.length === 0;

    const { data: dealerDropdownData = [], isLoading: isDealerDropdownLoading } = useGetPartsDealerBrandMatchDealerDropdownQuery({
        searchText: dealerSearchText
    });

    const { data: tableDealerDropdownData = [], isLoading: isTableDealerFilterLoading } = useGetPartsDealerBrandMatchDealerDropdownQuery({
        searchText: tableDealerSearchText
    });

    const { data: brandDropdownData = [], isLoading: isBrandDropdownLoading } = useGetPartsDealerBrandMatchBrandDropdownQuery({
        searchText: brandSearchText
    });

    useEffect(() => {
        (dealerDropdownData || []).forEach((option) => {
            if (option?.value != null) {
                selectedDealerOptionMapRef.current[option.value] = option;
            }
        });
    }, [dealerDropdownData]);

    useEffect(() => {
        (brandDropdownData || []).forEach((option) => {
            if (option?.value != null) {
                selectedBrandOptionMapRef.current[option.value] = option;
            }
        });
    }, [brandDropdownData]);

    const dealerOptions = useMemo(() => {
        const list = Array.isArray(dealerDropdownData) ? [...dealerDropdownData] : [];
        const existing = new Set(list.map((item) => item.value));
        selectedDealerIds.forEach((value) => {
            if (!existing.has(value) && selectedDealerOptionMapRef.current[value]) {
                list.push(selectedDealerOptionMapRef.current[value]);
            }
        });
        return list;
    }, [dealerDropdownData, selectedDealerIds]);

    const brandOptions = useMemo(() => {
        const list = Array.isArray(brandDropdownData) ? [...brandDropdownData] : [];
        const existing = new Set(list.map((item) => item.value));
        selectedBrands.forEach((value) => {
            if (!existing.has(value) && selectedBrandOptionMapRef.current[value]) {
                list.push(selectedBrandOptionMapRef.current[value]);
            }
        });
        return list;
    }, [brandDropdownData, selectedBrands]);

    const dealerFilterOptions = useMemo(() => (Array.isArray(tableDealerDropdownData) ? tableDealerDropdownData.filter((item) => !item.isBulkOption) : []), [tableDealerDropdownData]);

    const mappedSortField = SORT_FIELD_MAP[lazyParams.sortField] || undefined;
    const mappedSortOrder = lazyParams.sortOrder === 1 ? 'asc' : lazyParams.sortOrder === -1 ? 'desc' : undefined;

    const {
        data: listData,
        isLoading: isListLoading,
        isFetching: isListFetching,
        refetch: refetchList
    } = useGetPartsDealerBrandMatchListQuery({
        pageNumber: lazyParams.page,
        pageSize: lazyParams.rows,
        languageId,
        sortField: mappedSortField,
        sortOrder: mappedSortOrder,
        ...apiFilters
    });

    const [saveMatch, { isLoading: isSaving }] = useSavePartsDealerBrandMatchMutation();
    const [updateVisibility, { isLoading: isUpdating }] = useUpdatePartsDealerBrandMatchVisibilityMutation();
    const [deleteMatch, { isLoading: isDeleting }] = useDeletePartsDealerBrandMatchMutation();

    useEffect(() => {
        return () => {
            if (dealerDebounceRef.current) clearTimeout(dealerDebounceRef.current);
            if (brandDebounceRef.current) clearTimeout(brandDebounceRef.current);
            if (tableDealerDebounceRef.current) clearTimeout(tableDealerDebounceRef.current);
        };
    }, []);

    const handleDealerFilter = useCallback((event) => {
        const value = event?.filter || '';
        if (dealerDebounceRef.current) clearTimeout(dealerDebounceRef.current);
        dealerDebounceRef.current = setTimeout(() => {
            setDealerSearchText(value);
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    const handleBrandFilter = useCallback((event) => {
        const value = event?.filter || '';
        if (brandDebounceRef.current) clearTimeout(brandDebounceRef.current);
        brandDebounceRef.current = setTimeout(() => {
            setBrandSearchText(value);
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    const handleTableDealerFilterSearch = useCallback((value) => {
        setDealerFilterValue(value);
        if (tableDealerDebounceRef.current) clearTimeout(tableDealerDebounceRef.current);
        tableDealerDebounceRef.current = setTimeout(() => {
            setTableDealerSearchText(value);
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    const showApiMessage = useCallback(
        (severity, message) => {
            if (!message) return;
            toast.current?.show({
                severity,
                summary: severity === 'success' ? th('common.success') : th('common.error'),
                detail: message,
                life: 4000
            });
        },
        [th]
    );

    const openNewDialog = useCallback(() => {
        setSubmitted(false);
        setDialogVisible(true);
    }, []);

    const closeDialog = useCallback(() => {
        setDialogVisible(false);
        setSubmitted(false);
    }, []);

    const handleSave = useCallback(async () => {
        setSubmitted(true);

        if (!selectedDealerIds.length) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('partsDealerBrandMatch.requiredDealer'), life: 4000 });
            return;
        }
        if (!selectedBrands.length) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('partsDealerBrandMatch.requiredBrand'), life: 4000 });
            return;
        }
        if (isVisible === null || isVisible === undefined) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('partsDealerBrandMatch.requiredVisibility'), life: 4000 });
            return;
        }
        if (isLookupEmpty) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('partsDealerBrandMatch.visibilityLookupEmpty'), life: 4000 });
            return;
        }

        try {
            const response = await saveMatch({
                dealerIds: selectedDealerIds,
                brands: selectedBrands,
                isVisible
            }).unwrap();

            if (response?.isSuccessful === false) {
                showApiMessage('error', response?.errorMessage);
                return;
            }

            showApiMessage('success', response?.errorMessage);
            setSelectedDealerIds([]);
            setSubmitted(false);
            setDialogVisible(false);
        } catch {
            // HTTP hataları base.js içinde toast olarak gösterilir
        }
    }, [selectedDealerIds, selectedBrands, isVisible, isLookupEmpty, saveMatch, showApiMessage, th]);

    const handleVisibilityChange = useCallback(
        async (row, value) => {
            if (!row?.id || value === null || value === undefined || value === row.isVisible) return;

            try {
                const response = await updateVisibility({ id: row.id, isVisible: value }).unwrap();
                if (response?.isSuccessful === false) {
                    showApiMessage('error', response?.errorMessage);
                    return;
                }
                showApiMessage('success', response?.errorMessage);
            } catch {
                // HTTP hataları base.js içinde toast olarak gösterilir
            }
        },
        [updateVisibility, showApiMessage]
    );

    const confirmDelete = useCallback((item) => {
        setItemToDelete(item);
        setDeleteConfirmationVisible(true);
    }, []);

    const deleteItem = useCallback(async () => {
        if (!itemToDelete?.id) return;

        try {
            const response = await deleteMatch(itemToDelete.id).unwrap();
            if (response?.isSuccessful === false) {
                showApiMessage('error', response?.errorMessage);
                return;
            }
            showApiMessage('success', response?.errorMessage);
        } catch {
            // HTTP hataları base.js içinde toast olarak gösterilir
        } finally {
            setDeleteConfirmationVisible(false);
            setItemToDelete(null);
        }
    }, [itemToDelete, deleteMatch, showApiMessage]);

    const dealerTypeOptions = useMemo(
        () => [
            { label: th('partsDealerBrandMatch.domestic'), value: 1 },
            { label: th('partsDealerBrandMatch.foreign'), value: 2 }
        ],
        [th]
    );

    const activeOnlyOptions = useMemo(
        () => [
            { label: th('common.active'), value: 1 },
            { label: th('common.inactive'), value: 0 },
            { label: th('partsDealerBrandMatch.filterDeleted'), value: 2 }
        ],
        [th]
    );

    const handleTableFiltersChange = useCallback((updatedFilters) => {
        setActiveFilters(updatedFilters);
        setLazyParams((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleResetTableFilters = useCallback(() => {
        setActiveFilters(INITIAL_TABLE_FILTERS);
        setDealerFilterValue('');
        setTableDealerSearchText('');
        setLazyParams((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handlePage = useCallback((event) => {
        setLazyParams((prev) => ({
            ...prev,
            page: event.page + 1,
            rows: event.rows
        }));
    }, []);

    const handleSort = useCallback((event) => {
        setLazyParams((prev) => ({
            ...prev,
            page: 1,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    }, []);

    return {
        toast,
        dialogVisible,
        openNewDialog,
        closeDialog,
        refetchList,
        deleteConfirmationVisible,
        setDeleteConfirmationVisible,
        deleteItem,
        tableProps: {
            data: listData?.items || [],
            totalRecords: listData?.totalCount || 0,
            isLoading: isListLoading || isListFetching,
            onPage: handlePage,
            onSort: handleSort,
            lazyParams: {
                first: (lazyParams.page - 1) * lazyParams.rows,
                rows: lazyParams.rows,
                sortField: lazyParams.sortField,
                sortOrder: lazyParams.sortOrder
            },
            visibilityOptions,
            dealerTypeOptions,
            activeOnlyOptions,
            dealerFilterOptions,
            isDealerFilterLoading: isTableDealerFilterLoading,
            dealerFilterValue,
            onDealerFilterSearch: handleTableDealerFilterSearch,
            activeFilters,
            onFiltersChange: handleTableFiltersChange,
            onResetFilters: handleResetTableFilters,
            onVisibilityChange: handleVisibilityChange,
            onDelete: confirmDelete,
            isUpdating,
            isDeleting
        },
        formProps: {
            visible: dialogVisible,
            onHide: closeDialog,
            selectedDealerIds,
            onDealerChange: setSelectedDealerIds,
            dealerOptions,
            isDealerDropdownLoading,
            onDealerFilter: handleDealerFilter,
            selectedBrands,
            onBrandChange: setSelectedBrands,
            brandOptions,
            isBrandDropdownLoading,
            onBrandFilter: handleBrandFilter,
            isVisible,
            onVisibilityChange: setIsVisible,
            visibilityOptions,
            isLookupEmpty,
            isLookupLoading: isLookupLoading || isLookupFetching,
            onSave: handleSave,
            isSaving,
            submitted
        }
    };
};

export default usePartsDealerBrandMatch;
